import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { t } from "@/i18n";
import { calculatorOpen, lang } from "@/state/store";
import { evaluate } from "./calc";
import { focusAnswerInput, setAnswerDraft } from "./QuestionView";

const KEYS = ["7", "8", "9", "÷", "4", "5", "6", "×", "1", "2", "3", "−", "0", ",", "(", ")", "+"];

/** A small calculator the hero can use during battles and training. Fully keyboard driven. */
export function Calculator() {
  const expr = useSignal("");
  const ref = useRef<HTMLDivElement>(null);
  const L = lang.value;
  const result = evaluate(expr.value);
  const resultText = result === null ? "" : L === "pl" ? String(result).replace(".", ",") : String(result);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const press = (k: string) => {
    if (k === "C") expr.value = "";
    else if (k === "⌫") expr.value = expr.value.slice(0, -1);
    else expr.value += k;
  };
  const useResult = () => {
    if (result === null) return;
    setAnswerDraft(resultText);
    calculatorOpen.value = false;
    focusAnswerInput();
  };

  const onKey = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      calculatorOpen.value = false;
      focusAnswerInput();
    } else if (e.key === "Enter") {
      e.preventDefault();
      useResult();
    } else if (e.key === "Backspace") {
      e.preventDefault();
      press("⌫");
    } else if (e.key === "Delete") {
      press("C");
    } else if (/^[0-9()+\-*/.,]$/.test(e.key)) {
      e.preventDefault();
      press(e.key === "*" ? "×" : e.key === "/" ? "÷" : e.key === "-" ? "−" : e.key);
    }
  };

  return (
    <div class="calculator" ref={ref} tabIndex={0} onKeyDown={onKey}>
      <div class="calc-top">
        <span>🧮 {t("calc.title")}</span>
        <button
          onClick={() => {
            calculatorOpen.value = false;
            focusAnswerInput();
          }}
        >
          {t("calc.close")}
        </button>
      </div>
      <div class="calc-display">
        <div class="calc-expr">{expr.value || "0"}</div>
        <div class="calc-result">{resultText !== "" ? `= ${resultText}` : " "}</div>
      </div>
      <div class="calc-keys">
        {KEYS.map((k) => (
          <button key={k} type="button" onClick={() => press(k)}>
            {k}
          </button>
        ))}
        <button type="button" onClick={() => press("⌫")}>
          ⌫
        </button>
        <button type="button" onClick={() => press("C")}>
          C
        </button>
        <button type="button" class="primary wide" disabled={result === null} onClick={useResult}>
          {t("calc.use")} ⏎
        </button>
      </div>
    </div>
  );
}
